/**
 * API client: a fetch wrapper that attaches the Firebase ID token as Bearer,
 * plus a streaming `chat()` that consumes the SSE stream (CLAUDE.md §6) via
 * fetch + ReadableStream, parsing `token` / `field` / `done` / `error` events.
 */

import { getIdToken } from "./firebase";
import type {
  ChatRequest,
  DraftResponse,
  EmailListResponse,
  ExportFormat,
  FieldEvent,
  ProjectDoc,
  SubmissionRecord,
  SubmissionResponse,
} from "./types";

/**
 * In dev, Vite proxies `/api` -> localhost:8000, so a relative base works.
 * In prod the SPA is served from the same origin as the API (CLAUDE.md §3),
 * so relative also works. VITE_API_BASE_URL can override with an absolute URL.
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

function url(path: string): string {
  return `${API_BASE}${path}`;
}

async function authHeaders(
  extra: Record<string, string> = {},
): Promise<Record<string, string>> {
  const token = await getIdToken();
  const headers: Record<string, string> = { ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function parseError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    return data?.detail ?? data?.message ?? res.statusText;
  } catch {
    return res.statusText || `Request failed (${res.status})`;
  }
}

/** Core JSON request helper. */
async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = await authHeaders({
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  });
  const res = await fetch(url(path), { ...options, headers });
  if (!res.ok) throw new ApiError(await parseError(res), res.status);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Streaming chat (SSE over fetch)
// ---------------------------------------------------------------------------

export interface ChatCallbacks {
  /** Called for every `token` event with the prose delta. */
  onToken?: (text: string) => void;
  /** Called for every `field` event with a doc field update. */
  onField?: (name: FieldEvent["name"], value: FieldEvent["value"]) => void;
  /** Called once when the turn ends (`done`). */
  onDone?: () => void;
  /** Called on an `error` event or a transport failure. */
  onError?: (message: string) => void;
}

/**
 * Parse one SSE record (the block between blank lines).
 * Supports `event:` + (multi-line) `data:` fields per the SSE spec.
 */
function parseSSERecord(raw: string): { event: string; data: string } | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) continue; // comment / heartbeat
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).replace(/^ /, ""));
    }
  }
  if (dataLines.length === 0 && event === "message") return null;
  return { event, data: dataLines.join("\n") };
}

function dispatchEvent(
  event: string,
  data: string,
  cb: ChatCallbacks,
): boolean {
  // Returns true if the stream is finished (done/error).
  let payload: unknown = data;
  if (data) {
    try {
      payload = JSON.parse(data);
    } catch {
      /* keep raw string (e.g. plain token text) */
    }
  }

  switch (event) {
    case "token": {
      const text =
        typeof payload === "string"
          ? payload
          : ((payload as { text?: string })?.text ?? "");
      if (text) cb.onToken?.(text);
      return false;
    }
    case "field": {
      const p = payload as { name?: FieldEvent["name"]; value?: unknown };
      if (p?.name !== undefined) {
        cb.onField?.(p.name, p.value as FieldEvent["value"]);
      }
      return false;
    }
    case "done":
      cb.onDone?.();
      return true;
    case "error": {
      const message =
        typeof payload === "string"
          ? payload
          : ((payload as { message?: string })?.message ?? "Stream error.");
      cb.onError?.(message);
      return true;
    }
    default:
      return false;
  }
}

/**
 * POST to /api/chat and consume the SSE stream, invoking callbacks live.
 * Returns when the stream ends. Pass an AbortSignal to cancel mid-stream.
 */
export async function chat(
  body: ChatRequest,
  cb: ChatCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    const headers = await authHeaders({
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    });
    res = await fetch(url("/api/chat"), {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    cb.onError?.(
      err instanceof Error ? err.message : "Network error contacting the assistant.",
    );
    return;
  }

  if (!res.ok || !res.body) {
    cb.onError?.(await parseError(res));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Records are separated by a blank line (\n\n). Normalize CRLF.
      buffer = buffer.replace(/\r\n/g, "\n");
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const rawRecord = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseSSERecord(rawRecord);
        if (!parsed) continue;
        const finished = dispatchEvent(parsed.event, parsed.data, cb);
        if (finished) return;
      }
    }
    // Flush any trailing record without a terminating blank line.
    const tail = parseSSERecord(buffer.trim());
    if (tail) dispatchEvent(tail.event, tail.data, cb);
    cb.onDone?.();
  } catch (err) {
    if (signal?.aborted) return; // intentional cancel — not an error
    cb.onError?.(
      err instanceof Error ? err.message : "Streaming connection interrupted.",
    );
  } finally {
    reader.releaseLock();
  }
}

// ---------------------------------------------------------------------------
// Draft (doc) endpoints
// ---------------------------------------------------------------------------

export function getDraft(): Promise<DraftResponse> {
  return request<DraftResponse>("/api/doc", { method: "GET" });
}

export function saveDraft(doc: ProjectDoc): Promise<void> {
  // Backend PUT /api/doc takes the ProjectDoc as the raw body (not wrapped).
  return request<void>("/api/doc", {
    method: "PUT",
    body: JSON.stringify(doc),
  });
}

/** Clear the current user's draft (blank doc + empty conversation). */
export function resetDraft(): Promise<void> {
  return request<void>("/api/doc/reset", { method: "POST" });
}

/**
 * Export the doc CURRENTLY on screen as docx/pdf and trigger a browser download.
 * The live doc is POSTed so the file always matches the preview (no autosave race).
 */
export async function exportDoc(
  format: ExportFormat,
  doc: ProjectDoc,
): Promise<void> {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(url(`/api/doc/export?format=${format}`), {
    method: "POST",
    headers,
    body: JSON.stringify(doc),
  });
  if (!res.ok) throw new ApiError(await parseError(res), res.status);

  const blob = await res.blob();
  const safeCompany = (doc.companyName || "project").replace(/[^\w-]+/g, "_");
  const filename = `Project Planning Phase - ${safeCompany}.${format}`;

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------

export function submit(doc: ProjectDoc): Promise<SubmissionResponse> {
  return request<SubmissionResponse>("/api/submit", {
    method: "POST",
    body: JSON.stringify({ doc }),
  });
}

// ---------------------------------------------------------------------------
// Admin endpoints (CLAUDE.md §7/§8)
// ---------------------------------------------------------------------------

export const admin = {
  getEmailList(): Promise<EmailListResponse> {
    return request<EmailListResponse>("/api/admin/email-list", { method: "GET" });
  },
  addEmail(email: string): Promise<EmailListResponse> {
    return request<EmailListResponse>("/api/admin/email-list", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },
  removeEmail(email: string): Promise<EmailListResponse> {
    // Backend takes the email in the request body (not a path param).
    return request<EmailListResponse>("/api/admin/email-list", {
      method: "DELETE",
      body: JSON.stringify({ email }),
    });
  },
  listSubmissions(): Promise<{ submissions: SubmissionRecord[] }> {
    return request<{ submissions: SubmissionRecord[] }>(
      "/api/admin/submissions",
      { method: "GET" },
    );
  },
  deleteSubmission(submissionId: string): Promise<void> {
    return request<void>(
      `/api/admin/submissions/${encodeURIComponent(submissionId)}`,
      { method: "DELETE" },
    );
  },
  /** Transfer the admin role to another @aisingapore.org email. */
  transferAdmin(email: string): Promise<void> {
    return request<void>("/api/admin/transfer", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },
};
